/**
 * WhatsAppAdminPage — admin composer that generates personalized wa.me deep
 * links for the platform's WhatsApp number to send app-share invites and
 * promotions. No API integration — admin clicks each generated link (or the
 * "Open all" batch) to send from their own WhatsApp session.
 */
import React, { useEffect, useState } from 'react';
import { MessageCircle, Send, Users, Copy, ExternalLink, Loader2, History } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const AUDIENCE_OPTIONS = [
  { key: 'all',      label: 'All customers',           hint: 'Everyone with a saved phone' },
  { key: 'recent',   label: 'Recent (30d)',            hint: 'Ordered in the last 30 days' },
  { key: 'inactive', label: 'Inactive customers',      hint: 'No order in last N days' },
  { key: 'city',     label: 'By city',                 hint: 'Match saved profile city' },
];

const DEFAULT_TEMPLATE = "Hey {{first_name}}! 👋 We just added a bunch of new drivers on {{app}} — grab your next same-day delivery with 20% off. Book here: https://shipitnow.ca";

const WhatsAppAdminPage = () => {
  const [message, setMessage] = useState(DEFAULT_TEMPLATE);
  const [audience, setAudience] = useState('all');
  const [city, setCity] = useState('');
  const [inactiveDays, setInactiveDays] = useState(30);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adminWa, setAdminWa] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/admin/whatsapp/broadcasts', { params: { limit: 20 } });
      setHistory(data.broadcasts || []);
    } catch {/* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const buildPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post('/admin/whatsapp/broadcast/preview', {
        message,
        audience,
        city: audience === 'city' ? city : undefined,
        inactive_days: audience === 'inactive' ? Number(inactiveDays) : undefined,
        limit: 500,
      });
      setPreview(data);
      setAdminWa(data.admin_whatsapp || null);
      if (!data.recipients?.length) {
        toast.info('No matching customers with a WhatsApp phone number.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const openOne = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const openAll = () => {
    if (!preview?.recipients?.length) return;
    // Open the first 3 immediately; users typically batch via clipboard for the rest.
    preview.recipients.slice(0, 3).forEach((r) => openOne(r.open_url));
    toast.success(`Opened ${Math.min(3, preview.recipients.length)} — click the rest below one-by-one.`);
  };

  const copyAllLinks = async () => {
    if (!preview?.recipients?.length) return;
    const text = preview.recipients
      .map((r) => `${r.name || r.phone}\t${r.open_url}`)
      .join('\n');
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${preview.recipients.length} link(s) to clipboard.`);
  };

  const recordBroadcast = async () => {
    if (!preview?.recipients?.length) return;
    try {
      await api.post('/admin/whatsapp/broadcast/record', {
        message,
        audience,
        recipient_count: preview.recipients.length,
        recipients: preview.recipients.map((r) => ({
          user_id: r.user_id, phone: r.phone, name: r.name,
        })),
      });
      toast.success('Logged this broadcast in the audit history.');
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log broadcast');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="whatsapp-admin-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-emerald-600" />
          WhatsApp broadcast
        </h1>
        <p className="text-sm text-slate-500">
          Share the app and promos with your customers via WhatsApp. We generate a personalized
          <code className="mx-1 px-1 py-0.5 rounded bg-slate-100 text-[11px]">wa.me</code>
          link for each recipient — click to send from your own WhatsApp.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Composer */}
        <section className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-800">Message template</label>
            <p className="text-[11px] text-slate-500 mb-1">
              Placeholders: <code>{'{{first_name}}'}</code>, <code>{'{{name}}'}</code>, <code>{'{{app}}'}</code>
            </p>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              data-testid="whatsapp-message"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-800">Audience</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {AUDIENCE_OPTIONS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAudience(a.key)}
                  data-testid={`whatsapp-audience-${a.key}`}
                  className={`text-left p-3 rounded-xl border transition-colors ${
                    audience === a.key
                      ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-900">{a.label}</div>
                  <div className="text-[11px] text-slate-500">{a.hint}</div>
                </button>
              ))}
            </div>
            {audience === 'city' && (
              <Input
                className="mt-2"
                placeholder="City (Ottawa or Ibadan)"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="whatsapp-city-input"
              />
            )}
            {audience === 'inactive' && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  className="max-w-28"
                  value={inactiveDays}
                  onChange={(e) => setInactiveDays(e.target.value)}
                  data-testid="whatsapp-inactive-days"
                />
                <span className="text-sm text-slate-500">days without an order</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              onClick={buildPreview}
              disabled={loading || !message.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="whatsapp-preview-btn"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Building…</> : <><Users className="h-4 w-4 mr-1" /> Preview recipients</>}
            </Button>
            {adminWa && (
              <div className="text-[11px] text-slate-500">
                Sender: <span className="font-mono">+{adminWa}</span>
              </div>
            )}
            {!adminWa && (
              <div className="text-[11px] text-amber-600">
                Set your platform WhatsApp number in Admin → Settings → WhatsApp
              </div>
            )}
          </div>
        </section>

        {/* Preview + send */}
        <section className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5" data-testid="whatsapp-preview-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">
              {preview ? `${preview.count} recipient${preview.count === 1 ? '' : 's'}` : 'Preview'}
            </h2>
            {preview?.recipients?.length ? (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={copyAllLinks} data-testid="whatsapp-copy-links">
                  <Copy className="h-3 w-3 mr-1" /> Copy links
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openAll} data-testid="whatsapp-open-all">
                  <Send className="h-3 w-3 mr-1" /> Open first 3
                </Button>
              </div>
            ) : null}
          </div>

          {!preview ? (
            <div className="text-sm text-slate-400">Click &quot;Preview recipients&quot; to build a personalized link list.</div>
          ) : preview.recipients.length === 0 ? (
            <div className="text-sm text-slate-500">No customers matched. Try a different audience.</div>
          ) : (
            <>
              <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="whatsapp-recipient-list">
                {preview.recipients.slice(0, 200).map((r) => (
                  <div key={r.user_id} className="py-2 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{r.name || '—'}</div>
                      <div className="text-[11px] text-slate-500 font-mono">+{r.phone}</div>
                    </div>
                    <a
                      href={r.open_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800"
                      data-testid={`whatsapp-open-${r.user_id}`}
                    >
                      <ExternalLink className="h-3 w-3" /> Send
                    </a>
                  </div>
                ))}
                {preview.recipients.length > 200 && (
                  <div className="pt-2 text-[11px] text-slate-400">…and {preview.recipients.length - 200} more.</div>
                )}
              </div>
              <div className="pt-3 mt-3 border-t border-slate-100 text-right">
                <Button size="sm" variant="ghost" onClick={recordBroadcast} data-testid="whatsapp-log-btn">
                  <History className="h-3 w-3 mr-1" /> Log this broadcast
                </Button>
              </div>
            </>
          )}
        </section>
      </div>

      {history.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Broadcast history</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{h.message_template}</div>
                  <div className="text-[11px] text-slate-500">
                    {h.audience} · {h.recipient_count} recipient{h.recipient_count === 1 ? '' : 's'} · {new Date(h.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default WhatsAppAdminPage;
